import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { formatCurrency } from "@/lib/currency";
import { readJsonArrayOrEmpty } from "@/lib/http";
import { sortSalesNewestFirst } from "@/lib/sales-order";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "./dashboard-money-visibility";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Scissors, Package, TrendingUp, Users, BarChart3,
  ArrowRight, Ruler, ClipboardList, AlertTriangle, Receipt,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function TailorDashboard() {
  const { user, store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const branchQuery = activeBranchId ? `?branchId=${activeBranchId}` : "";
  const isOwner = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isOwner;
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility();

  const [stats, setStats] = useState({
    todayRevenue: 0, todayOrders: 0,
    monthOrders: 0, monthRevenue: 0,
    totalClients: 0, totalFabrics: 0, lowStockFabrics: 0,
  });
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [fabricStock, setFabricStock] = useState<any[]>([]);
  const [lowStockItems, setLowStockItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lagosDate = (value: Date | string) => new Date(value).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
        const today = lagosDate(new Date());
        const currentMonth = today.slice(0, 7);
        const [salesRes, productsRes, customersRes] = await Promise.allSettled([
          fetch(`${BASE}/api/sales${branchQuery}`, { credentials: "include", cache: "no-store" }),
          fetch(`${BASE}/api/products${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/customers`, { credentials: "include" }),
        ]);
        const sales = salesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(salesRes.value) : [];
        const products = productsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(productsRes.value) : [];
        const allCustomers = customersRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(customersRes.value) : [];
        const branchCustomerIds = new Set((sales as any[]).map((s: any) => s.customerId).filter(Boolean));
        const customers = activeBranchId
          ? (allCustomers as any[]).filter((c: any) => branchCustomerIds.has(c.id))
          : allCustomers;

        const newestSales = sortSalesNewestFirst(sales);
        const todaySales = newestSales.filter((s: any) => lagosDate(s.createdAt) === today);
        const monthSales = newestSales.filter((s: any) => lagosDate(s.createdAt).startsWith(currentMonth));
        const activeProducts = products as any[];
        const low = activeProducts.filter((p: any) => p.stock <= (p.reorderLevel ?? 3));

        setStats({
          todayRevenue: todaySales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          todayOrders: todaySales.length,
          monthOrders: monthSales.length,
          monthRevenue: monthSales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          totalClients: (customers as any[]).length,
          totalFabrics: activeProducts.length,
          lowStockFabrics: low.length,
        });
        setRecentOrders(newestSales.slice(0, 6));
        setFabricStock(activeProducts.slice(0, 5));
        setLowStockItems(low.slice(0, 4));
      } finally { setLoading(false); }
    };
    load();
  }, [branchQuery]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">🧵</span>
            <h1 className="text-2xl font-bold">{store?.name || "My Atelier"}</h1>
            <Badge className="bg-teal-100 text-teal-700 border-teal-200">Fashion Designer</Badge>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Custom Orders & Atelier Dashboard · {activeBranch?.name ?? "All Branches"} · {new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          <Link href="/customers"><Button variant="outline" size="sm" className="gap-1.5"><Users className="w-3.5 h-3.5" />Clients</Button></Link>
          <Link href="/sales/new"><Button size="sm" className="gap-1.5 bg-teal-600 hover:bg-teal-700"><Scissors className="w-3.5 h-3.5" />New Custom Order</Button></Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today's Revenue" value={maskMoney(formatCurrency(stats.todayRevenue))} icon={TrendingUp} color="bg-teal-100 text-teal-600" sub={`${stats.todayOrders} order${stats.todayOrders !== 1 ? "s" : ""} taken`} />
        <StatCard label="Orders This Month" value={String(stats.monthOrders)} icon={ClipboardList} color="bg-indigo-100 text-indigo-600" sub={`${maskMoney(formatCurrency(stats.monthRevenue))} earned`} />
        <StatCard label="Total Clients" value={String(stats.totalClients)} icon={Users} color="bg-blue-100 text-blue-600" sub="registered clients" />
        <StatCard
          label="Fabric & Materials"
          value={String(stats.totalFabrics)}
          icon={stats.lowStockFabrics > 0 ? AlertTriangle : Package}
          color={stats.lowStockFabrics > 0 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}
          sub={stats.lowStockFabrics > 0 ? `${stats.lowStockFabrics} running low` : "All stocked"}
        />
      </div>

      {/* Low stock alert banner */}
      {lowStockItems.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800">Low fabric/material stock</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {lowStockItems.map((p: any) => p.name).join(", ")} — restock before taking more orders
            </p>
          </div>
          <Link href="/inventory"><Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs">Restock</Button></Link>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "New Custom Order", href: "/sales/new",    icon: Scissors,       color: "bg-teal-50 border-teal-200 hover:bg-teal-100"     },
            { label: "Client Records",   href: "/customers",    icon: Users,          color: "bg-blue-50 border-blue-200 hover:bg-blue-100"     },
            { label: "Fabric & Materials",href: "/inventory",   icon: Ruler,          color: "bg-indigo-50 border-indigo-200 hover:bg-indigo-100"},
            { label: "Daily Expenses",   href: "/expenses",     icon: Receipt,        color: "bg-amber-50 border-amber-200 hover:bg-amber-100", mgr: true },
          ].filter((a: any) => !a.mgr || isManager).map(a => (
            <Link key={a.label} href={a.href}>
              <div className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors text-slate-800 ${a.color}`}>
                <a.icon className="w-5 h-5 text-slate-700" />
                <span className="text-sm font-semibold">{a.label}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Fabric & Materials Stock */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Ruler className="w-4 h-4 text-teal-500" />Fabric & Materials
            </h3>
            <Link href="/inventory"><span className="text-xs text-primary hover:underline cursor-pointer">Manage →</span></Link>
          </div>
          {fabricStock.length === 0 ? (
            <div className="text-center py-6">
              <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No fabrics or materials added yet</p>
              <Link href="/inventory?add=1"><Button size="sm" className="mt-3 bg-teal-600 hover:bg-teal-700">Add First Material</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {fabricStock.map((p: any) => {
                const threshold = p.reorderLevel ?? 3;
                const isLow = p.stock <= threshold;
                return (
                  <div key={p.id} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {isLow && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">Low</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">{p.sku || "No SKU"}</div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="text-sm font-semibold">{formatCurrency(Number(p.price))}</div>
                      <div className={`text-xs font-medium ${isLow ? "text-amber-600" : "text-emerald-600"}`}>{p.stock} in stock</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Custom Orders */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-indigo-500" />Recent Custom Orders
            </h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">View all →</span></Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="text-center py-8">
              <Scissors className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No custom orders yet</p>
              <Link href="/sales/new"><Button size="sm" className="mt-3 bg-teal-600 hover:bg-teal-700">Take First Order</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentOrders.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.customerName ?? "Walk-in Client"}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.receiptNumber} · {new Date(s.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
                    </div>
                  </div>
                  <div className="text-sm font-semibold ml-3">{maskMoney(formatCurrency(Number(s.totalAmount ?? s.total ?? 0)))}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="bg-muted/40 border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-teal-500" />Owner Analytics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Profit & Loss",       href: "/accounting/profit-loss" },
              { label: "Client Reports",       href: "/people/customer-reports" },
              { label: "Staff Performance",    href: "/people/staff-performance" },
              { label: "Expense Reports",      href: "/accounting/expense-reports" },
            ].map(l => (
              <Link key={l.label} href={l.href}>
                <Button variant="outline" size="sm" className="w-full text-xs">{l.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
